import React, { useState, useEffect, useRef } from 'react';
import {
  Flex,
  Typography,
  Button,
  Card,
  Table,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Tabs,
  Row,
  Col,
  Spin,
  Space,
  Pagination,
} from 'antd';
import {
  CarOutlined,
  DollarOutlined,
  RiseOutlined,
  CalendarOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  DownloadOutlined,
  EditOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  PictureOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/TranslationContext';
import { useNotification } from '../../hooks/useNotification';
import DashboardService from '../../service';
import VinCopy from '../../components/VinCopy';
import LotCopy from '../../components/LotCopy';
import { useIsMobile, useIsTablet } from '../../hooks/useResponsive';

const AUCTION_NAMES = {
  1: 'IAAI US',
  2: 'Copart',
  3: 'Manheim',
  5: 'ACV',
  11: 'IAAI Canada'
};

const WinCarsPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [winCars, setWinCars] = useState([]);
  const [managers, setManagers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [subdealers, setSubdealers] = useState([]);
  const [filteredCars, setFilteredCars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBranch, setFilterBranch] = useState(undefined);
  const [filterManager, setFilterManager] = useState(undefined);
  const [filterDealer, setFilterDealer] = useState(undefined);
  const [selectedCar, setSelectedCar] = useState(null);
  const [carDetailOpen, setCarDetailOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [assignType, setAssignType] = useState('dealer');
  const [assignToId, setAssignToId] = useState('');
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [selectedSubdealer, setSelectedSubdealer] = useState(null);
  const [dealerSearchResults, setDealerSearchResults] = useState([]);
  const [subdealerSearchResults, setSubdealerSearchResults] = useState([]);
  const [dealerSearchLoading, setDealerSearchLoading] = useState(false);
  const [subdealerSearchLoading, setSubdealerSearchLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [branchFilterResults, setBranchFilterResults] = useState([]);
  const [managerFilterResults, setManagerFilterResults] = useState([]);
  const [dealerFilterResults, setDealerFilterResults] = useState([]);
  const [managerFilterLoading, setManagerFilterLoading] = useState(false);
  const [dealerFilterLoading, setDealerFilterLoading] = useState(false);
  const [dealerSearchWaiting, setDealerSearchWaiting] = useState(false);
  const [subdealerSearchWaiting, setSubdealerSearchWaiting] = useState(false);
  const [managerFilterWaiting, setManagerFilterWaiting] = useState(false);
  const [dealerFilterWaiting, setDealerFilterWaiting] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('Unpaid');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [deletingImage, setDeletingImage] = useState(null);
  const [purchasedViewTab, setPurchasedViewTab] = useState('our_dealers');
  const fileInputRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const dealerSearchDebounceRef = useRef(null);
  const subdealerSearchDebounceRef = useRef(null);
  const managerFilterDebounceRef = useRef(null);
  const dealerFilterDebounceRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const normalizePurchasedLots = (lots) => {
    return lots.map(lot => ({
      ...lot,
      id: lot._id || lot.id,
      lotNumber: lot.lotNumber || lot.lot_number,
      winningBid: lot.winningBid || lot.purchasePriceInDollars || (lot.purchasePrice ? lot.purchasePrice / 100 : 0),
      fees: lot.fees || (lot.purchasePriceFeeInDollars && lot.purchasePriceInDollars ? lot.purchasePriceFeeInDollars - lot.purchasePriceInDollars : 0) || (lot.purchasePriceFee && lot.purchasePrice ? (lot.purchasePriceFee - lot.purchasePrice) / 100 : 0) || 0,
      totalCost: lot.totalCost || lot.purchasePriceFeeInDollars || (lot.purchasePriceFee ? lot.purchasePriceFee / 100 : 0) || lot.purchasePriceInDollars || (lot.purchasePrice ? lot.purchasePrice / 100 : 0),
      wonByName: lot.wonByName || lot.userName || lot.dealerName || 'Unknown',
      wonDate: lot.wonDate || lot.saleDate || lot.createdAt,
      auctionSite: lot.auctionSite || AUCTION_NAMES[+lot.auctionId] || 'Unknown',
      status: lot.status || lot.titleStatus || 'pending_delivery',
      location: lot.location || 'Unknown',
      mileage: lot.mileage || 0,
      color: lot.color || 'N/A',
      condition: lot.condition || 'N/A'
    }));
  };

  const loadPurchasedLots = async (page = 1) => {
    try {
      setLoading(true);
      setFilteredCars([]);

      const filterParams = {
        page,
        limit,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        view: purchasedViewTab
      };

      if (filterBranch && filterBranch !== '') {
        filterParams.branchId = parseInt(filterBranch);
      }
      if (filterManager && filterManager !== '') {
        filterParams.managerId = filterManager;
      }
      if (filterDealer && filterDealer !== '') {
        filterParams.dealerId = filterDealer;
      }
      if (searchTerm && searchTerm.trim() !== '') {
        filterParams.quickSearch = searchTerm.trim();
      }

      let purchasedLotsResponse;

      switch (user.role) {
        case 'super_admin':
          purchasedLotsResponse = await DashboardService.getPurchasedLots(filterParams);
          break;
        case 'manager':
          if (!filterParams.branchId) {
            filterParams.branchId = user.branchId;
          }
          purchasedLotsResponse = await DashboardService.getPurchasedLots(filterParams);
          break;
        case 'dealer':
          if (!filterParams.dealerId) {
            filterParams.dealerId = user.user_id || user.dealerId;
          }
          purchasedLotsResponse = await DashboardService.getPurchasedLots(filterParams);
          break;
        case 'subdealer':
          if (searchTerm && searchTerm.trim() !== '') {
            purchasedLotsResponse = await DashboardService.getMyPurchasedLots({ page, limit, quickSearch: searchTerm.trim() });
          } else {
            purchasedLotsResponse = await DashboardService.getMyPurchasedLots({ page, limit });
          }
          break;
        default:
          if (searchTerm && searchTerm.trim() !== '') {
            purchasedLotsResponse = await DashboardService.getMyPurchasedLots({ page, limit, quickSearch: searchTerm.trim() });
          } else {
            purchasedLotsResponse = await DashboardService.getMyPurchasedLots({ page, limit });
          }
      }

      if (purchasedLotsResponse?.success && purchasedLotsResponse.lots) {
        const normalizedLots = normalizePurchasedLots(purchasedLotsResponse.lots);
        setWinCars(normalizedLots);
        setFilteredCars(normalizedLots);
        if (purchasedLotsResponse.pagination) {
          setPagination(purchasedLotsResponse.pagination);
        }
      } else if (purchasedLotsResponse?.lots) {
        const normalizedLots = normalizePurchasedLots(purchasedLotsResponse.lots);
        setWinCars(normalizedLots);
        setFilteredCars(normalizedLots);
        if (purchasedLotsResponse.pagination) {
          setPagination(purchasedLotsResponse.pagination);
        }
      }
    } catch (error) {
      console.error('Error loading win cars data:', error);
      showError('Failed to load purchased lots. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const branchesResponse = await DashboardService.getBranches();
        if (branchesResponse.success && branchesResponse.branches) {
          setBranchFilterResults(branchesResponse.branches);
        }

        if (user.role === 'super_admin' || user.role === 'manager') {
          const managersResponse = await DashboardService.getManagers(1, 50);
          if (managersResponse.success && managersResponse.users) {
            setManagers(managersResponse.users);
            setManagerFilterResults(managersResponse.users);
          } else if (managersResponse.users) {
            setManagers(managersResponse.users);
            setManagerFilterResults(managersResponse.users);
          }
        }

        if (user.role !== 'subdealer') {
          const dealersResponse = await DashboardService.getDealers(1, 50);
          if (dealersResponse.success && dealersResponse.users) {
            setDealers(dealersResponse.users);
            setDealerSearchResults(dealersResponse.users);
            setDealerFilterResults(dealersResponse.users);
          } else if (dealersResponse.users) {
            setDealers(dealersResponse.users);
            setDealerSearchResults(dealersResponse.users);
            setDealerFilterResults(dealersResponse.users);
          }
        }

        if (user.role !== 'subdealer') {
          const subdealersResponse = await DashboardService.getSubdealers(1, 50);
          if (subdealersResponse.success && subdealersResponse.users) {
            setSubdealers(subdealersResponse.users);
            setSubdealerSearchResults(subdealersResponse.users);
          } else if (subdealersResponse.users) {
            setSubdealers(subdealersResponse.users);
            setSubdealerSearchResults(subdealersResponse.users);
          }
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };

    if (user) {
      loadInitialData();
      loadPurchasedLots(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (user && currentPage > 0) {
      loadPurchasedLots(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 1500);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchInput]);

  useEffect(() => {
    setFilteredCars(winCars);
  }, [winCars]);

  useEffect(() => {
    if (user) {
      setCurrentPage(1);
      loadPurchasedLots(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBranch, filterManager, filterDealer, searchTerm, purchasedViewTab]);

  const handleCarClick = (car) => {
    setSelectedCar(car);
    setCarDetailOpen(true);
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !selectedCar) return;

    if (files.length > 10) {
      showError('Maximum 10 images allowed per upload');
      return;
    }

    setUploadingImages(true);
    try {
      const result = await DashboardService.uploadPurchasedLotImages(selectedCar.lotNumber, files);

      if (result.success) {
        const updatedCar = { ...selectedCar, images: result.lot.images || [] };
        setSelectedCar(updatedCar);
        await loadPurchasedLots(currentPage);
        showSuccess(`Successfully uploaded ${files.length} image(s)!`);
      } else {
        showError(result.error || 'Failed to upload images');
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      showError('Error uploading images');
    } finally {
      setUploadingImages(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageDelete = async (imageKey) => {
    if (!selectedCar || !window.confirm('Are you sure you want to delete this image?')) return;

    setDeletingImage(imageKey);
    try {
      const result = await DashboardService.deletePurchasedLotImage(selectedCar.lotNumber, imageKey);

      if (result.success) {
        const updatedCar = { ...selectedCar, images: result.lot.images || [] };
        setSelectedCar(updatedCar);
        await loadPurchasedLots(currentPage);
        showSuccess('Image deleted successfully!');
      } else {
        showError(result.error || 'Failed to delete image');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      showError('Error deleting image');
    } finally {
      setDeletingImage(null);
    }
  };

  const handleEditClick = (car, e) => {
    if (e) e.stopPropagation();
    setEditingCar(car);
    setPaymentStatus(car.paymentStatus || 'Unpaid');

    if (car.subdealerId) {
      setAssignType('subdealer');
      const userId = car.userId || car.user_id || '';
      setAssignToId(userId);
      const sub = subdealers.find(s => (s.user_id || s.id) === userId);
      setSelectedSubdealer(sub || null);
      setSelectedDealer(null);
    } else if (car.dealerId) {
      setAssignType('dealer');
      const dealerId = car.dealerId?.toString() || '';
      setAssignToId(dealerId);
      const dealer = dealers.find(d => {
        const id = d.dealerId || d.autoIncrementId || d.id;
        return id?.toString() === dealerId || d.user_id === dealerId;
      });
      setSelectedDealer(dealer || null);
      setSelectedSubdealer(null);
    } else {
      setAssignType('dealer');
      setAssignToId('');
      setSelectedDealer(null);
      setSelectedSubdealer(null);
    }
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingCar(null);
    setAssignType('dealer');
    setAssignToId('');
    setSelectedDealer(null);
    setSelectedSubdealer(null);
    setDealerSearchResults(dealers);
    setSubdealerSearchResults(subdealers);
    setPaymentStatus('Unpaid');
  };

  const handleDealerSearch = async (searchTerm, reason) => {
    if (reason === 'reset' || reason === 'clear') {
      setDealerSearchResults(dealers);
      setDealerSearchLoading(false);
      setDealerSearchWaiting(false);
      return;
    }

    if (dealerSearchDebounceRef.current) {
      clearTimeout(dealerSearchDebounceRef.current);
    }

    if (!searchTerm || searchTerm.trim().length < 2) {
      setDealerSearchResults(dealers);
      setDealerSearchLoading(false);
      setDealerSearchWaiting(false);
      return;
    }

    setDealerSearchWaiting(true);
    dealerSearchDebounceRef.current = setTimeout(async () => {
      try {
        setDealerSearchWaiting(false);
        setDealerSearchLoading(true);
        const response = await DashboardService.searchUsers({
          quickSearch: searchTerm,
          role: 'dealer',
          page: 1,
          limit: 100
        });

        if (response.success && response.users) {
          setDealerSearchResults(response.users);
        } else if (response.users) {
          setDealerSearchResults(response.users);
        }
      } catch (error) {
        console.error('Error searching dealers:', error);
        setDealerSearchResults(dealers);
      } finally {
        setDealerSearchLoading(false);
      }
    }, 1500);
  };

  const handleSubdealerSearch = async (searchTerm, reason) => {
    if (reason === 'reset' || reason === 'clear') {
      setSubdealerSearchResults(subdealers);
      setSubdealerSearchLoading(false);
      setSubdealerSearchWaiting(false);
      return;
    }

    if (subdealerSearchDebounceRef.current) {
      clearTimeout(subdealerSearchDebounceRef.current);
    }

    if (!searchTerm || searchTerm.trim().length < 2) {
      setSubdealerSearchResults(subdealers);
      setSubdealerSearchLoading(false);
      setSubdealerSearchWaiting(false);
      return;
    }

    setSubdealerSearchWaiting(true);
    subdealerSearchDebounceRef.current = setTimeout(async () => {
      try {
        setSubdealerSearchWaiting(false);
        setSubdealerSearchLoading(true);
        const response = await DashboardService.searchUsers({
          quickSearch: searchTerm,
          role: 'subdealer',
          page: 1,
          limit: 100
        });

        if (response.success && response.users) {
          setSubdealerSearchResults(response.users);
        } else if (response.users) {
          setSubdealerSearchResults(response.users);
        }
      } catch (error) {
        console.error('Error searching subdealers:', error);
        setSubdealerSearchResults(subdealers);
      } finally {
        setSubdealerSearchLoading(false);
      }
    }, 1500);
  };

  const handleUpdateAssignment = async () => {
    if (!assignToId || assignToId.trim() === '') {
      showError('Please select a dealer or subdealer');
      return;
    }

    if (!paymentStatus || !['Paid', 'Unpaid'].includes(paymentStatus)) {
      showError('Please select a valid payment status');
      return;
    }

    try {
      setUpdating(true);

      let updateData = { paymentStatus };

      if (assignType === 'dealer') {
        const isNumeric = /^\d+$/.test(assignToId);
        updateData.dealerId = isNumeric ? parseInt(assignToId) : assignToId;
      } else {
        updateData.userId = assignToId;
      }

      const result = await DashboardService.updatePurchasedLot(editingCar.lotNumber, updateData);

      if (result.success) {
        await loadPurchasedLots(currentPage);
        handleCloseEditModal();
        showSuccess('Car updated successfully!');
      } else {
        showError(result.error || 'Failed to update car');
      }
    } catch (error) {
      console.error('Error updating car:', error);
      showError('Error updating car');
    } finally {
      setUpdating(false);
    }
  };

  const handleManagerFilterSearch = async (searchTerm) => {
    if (managerFilterDebounceRef.current) {
      clearTimeout(managerFilterDebounceRef.current);
    }

    if (!searchTerm || searchTerm.trim().length < 2) {
      setManagerFilterResults(managers);
      setManagerFilterLoading(false);
      setManagerFilterWaiting(false);
      return;
    }

    setManagerFilterWaiting(true);
    managerFilterDebounceRef.current = setTimeout(async () => {
      try {
        setManagerFilterWaiting(false);
        setManagerFilterLoading(true);
        const response = await DashboardService.searchUsers({
          quickSearch: searchTerm,
          role: 'manager',
          page: 1,
          limit: 100
        });

        if (response.success && response.users) {
          setManagerFilterResults(response.users);
        } else if (response.users) {
          setManagerFilterResults(response.users);
        }
      } catch (error) {
        console.error('Error searching managers:', error);
      } finally {
        setManagerFilterLoading(false);
      }
    }, 1500);
  };

  const handleDealerFilterSearch = async (searchTerm) => {
    if (dealerFilterDebounceRef.current) {
      clearTimeout(dealerFilterDebounceRef.current);
    }

    if (!searchTerm || searchTerm.trim().length < 2) {
      setDealerFilterResults(dealers);
      setDealerFilterLoading(false);
      setDealerFilterWaiting(false);
      return;
    }

    setDealerFilterWaiting(true);
    dealerFilterDebounceRef.current = setTimeout(async () => {
      try {
        setDealerFilterWaiting(false);
        setDealerFilterLoading(true);
        const response = await DashboardService.searchUsers({
          quickSearch: searchTerm,
          role: 'dealer',
          page: 1,
          limit: 100
        });

        if (response.success && response.users) {
          setDealerFilterResults(response.users);
        } else if (response.users) {
          setDealerFilterResults(response.users);
        }
      } catch (error) {
        console.error('Error searching dealers:', error);
      } finally {
        setDealerFilterLoading(false);
      }
    }, 1500);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'success';
      case 'in_transit': return 'warning';
      case 'pending_delivery': return 'processing';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return '✓';
      case 'in_transit': return '🚚';
      case 'pending_delivery': return '⏳';
      default: return '?';
    }
  };

  const calculateStats = () => {
    const totalCars = pagination?.totalLots ?? filteredCars.length;
    const totalValue = filteredCars.reduce((sum, car) => sum + (car.totalCost || 0), 0);
    const deliveredCars = filteredCars.filter(car => car.status === 'delivered').length;
    const avgValue = filteredCars.length > 0 ? totalValue / filteredCars.length : 0;
    return { totalCars, totalValue, deliveredCars, avgValue };
  };

  const stats = calculateStats();

  const exportData = () => {
    const csvContent = [
      ['Lot Number', 'VIN', 'Make', 'Model', 'Year', 'Winning Bid', 'Total Cost', 'Won By', 'Status', 'Won Date'].join(','),
      ...filteredCars.map(car => [
        car.lotNumber,
        car.vin,
        car.make,
        car.model,
        car.year,
        car.winningBid,
        car.totalCost,
        car.wonByName,
        car.status,
        car.wonDate
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `win-cars-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const branchOptions = branchFilterResults.map(b => ({
    value: b.id?.toString(),
    label: `${b.flag || ''} ${b.name}`,
    ...b
  }));

  const managerFilterOptions = managerFilterResults.map(m => ({
    value: m.user_id || m.id?.toString(),
    label: m.name || '',
    ...m
  }));

  const dealerFilterOptions = dealerFilterResults.map(d => ({
    value: d.user_id || d.id?.toString(),
    label: d.name || '',
    ...d
  }));

  const dealerOptionsForSelect = (() => {
    const list = selectedDealer && !dealerSearchResults.some(d =>
      (d.dealerId || d.autoIncrementId || d.id) === (selectedDealer.dealerId || selectedDealer.autoIncrementId || selectedDealer.id)
    ) ? [selectedDealer, ...dealerSearchResults] : dealerSearchResults;
    return list.map(d => {
      const id = d.dealerId || d.autoIncrementId || d.id || d.user_id;
      return { value: id?.toString(), label: `${d.name || ''} - ${d.branchName || ''}`, data: d };
    });
  })();

  const subdealerOptionsForSelect = (() => {
    const list = selectedSubdealer && !subdealerSearchResults.some(s =>
      (s.user_id || s.id) === (selectedSubdealer.user_id || selectedSubdealer.id)
    ) ? [selectedSubdealer, ...subdealerSearchResults] : subdealerSearchResults;
    return list.map(s => ({
      value: s.user_id || s.id,
      label: `${s.name || ''} - ${s.dealerName || ''} (${s.branchName || ''})`,
      data: s
    }));
  })();

  const columns = [
    {
      title: 'Car Details',
      key: 'carDetails',
      render: (_, car) => {
        const carImage = car.images && car.images.length > 0 ? car.images[0].url : null;
        return (
          <Flex align="center" gap={8}>
            {carImage ? (
              <img
                src={carImage}
                alt={`${car.year} ${car.make} ${car.model}`}
                style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div style={{ width: 80, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: 4, border: '1px solid #d9d9d9' }}>
                <CarOutlined style={{ fontSize: 32, color: '#bfbfbf' }} />
              </div>
            )}
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{car.year} {car.make} {car.model}</div>
              <Flex align="center" gap={4} wrap="wrap">
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>VIN: {car.vin}</Typography.Text>
                {car.vin && <VinCopy vin={car.vin} size="small" />}
              </Flex>
              <Flex align="center" gap={4} wrap="wrap">
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>Lot: {car.lotNumber}</Typography.Text>
                {car.lotNumber && <LotCopy lotNumber={car.lotNumber} size="small" />}
              </Flex>
              {(isMobile || isTablet) && (
                <div style={{ marginTop: 4 }}>
                  <Tag color="blue">{car.auctionSite}</Tag>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>${(car.totalCost || 0).toLocaleString()}</Typography.Text>
                  <div style={{ marginTop: 4 }}>
                    <Tag icon={car.paymentStatus === 'Paid' ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={car.paymentStatus === 'Paid' ? 'success' : 'error'}>
                      {car.paymentStatus || 'Unpaid'}
                    </Tag>
                    {car.paymentStatus !== 'Paid' && car.unpaidAmountInDollars > 0 && (
                      <Typography.Text type="danger" style={{ fontSize: 12, marginLeft: 4 }}>Unpaid: ${(car.unpaidAmountInDollars || 0).toLocaleString()}</Typography.Text>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Flex>
        );
      }
    },
    {
      title: 'Auction Info',
      key: 'auctionInfo',
      responsive: ['md'],
      render: (_, car) => (
        <div>
          <Tag color="blue">{car.auctionSite}</Tag>
          <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>Bid: ${(car.winningBid || 0).toLocaleString()}</Typography.Text></div>
          <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>Fees: ${(car.fees || 0).toLocaleString()}</Typography.Text></div>
        </div>
      )
    },
    {
      title: 'Winning Details',
      key: 'winningDetails',
      responsive: ['lg'],
      render: (_, car) => (
        <div>
          <div style={{ fontWeight: 600 }}>Won by: {car.wonByName}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Total: ${(car.totalCost || 0).toLocaleString()}</Typography.Text>
          <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>Date: {new Date(car.saleUtcDatetime).toLocaleDateString()}</Typography.Text></div>
        </div>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, car) => (
        <div>
          <Tag color={getStatusColor(car.status)}>{getStatusIcon(car.status)} {car.status.replace('_', ' ').toUpperCase()}</Tag>
          {car.deliveryDate && <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>Delivered: {new Date(car.deliveryDate).toLocaleDateString()}</Typography.Text></div>}
        </div>
      )
    },
    {
      title: 'Payment',
      key: 'payment',
      responsive: ['md'],
      render: (_, car) => (
        <div>
          <Tag icon={car.paymentStatus === 'Paid' ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={car.paymentStatus === 'Paid' ? 'success' : 'error'}>
            {car.paymentStatus || 'Unpaid'}
          </Tag>
          {car.paymentStatus !== 'Paid' && (
            <div><Typography.Text type={car.unpaidAmountInDollars > 0 ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>Unpaid: ${(car.unpaidAmountInDollars || 0).toLocaleString()}</Typography.Text></div>
          )}
        </div>
      )
    },
    {
      title: 'Location',
      key: 'location',
      responsive: ['md'],
      render: (_, car) => (
        <div>
          <div>{car.location}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{car.branchName}</Typography.Text>
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, car) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Button type="text" icon={<EyeOutlined />} onClick={() => handleCarClick(car)} title="View Details" />
          {(user.role === 'super_admin' || user.role === 'manager' || user.role === 'dealer') && (
            <Button type="text" icon={<EditOutlined />} onClick={(e) => handleEditClick(car, e)} title="Update Purchased Car" />
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 20 }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
        <Typography.Title level={3} style={{ margin: 0, color: '#1e3a5f' }}>Win Cars Management</Typography.Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()} title="Refresh" />
          <Button icon={<DownloadOutlined />} onClick={exportData}>Export CSV</Button>
        </Space>
      </Flex>

      {(user.role === 'super_admin' || user.role === 'manager') && (
        <Tabs
          activeKey={purchasedViewTab}
          onChange={(key) => {
            setPurchasedViewTab(key);
            setCurrentPage(1);
          }}
          style={{ marginBottom: 24 }}
          items={[
            { key: 'our_dealers', label: t('winCarsView.ourDealers') || 'Our Dealers' },
            { key: 'all', label: t('winCarsView.allAuctionWins') || 'All Auction Wins' }
          ]}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Flex align="center" gap={8}>
              <span style={{ color: '#1e3a5f', fontSize: 20 }}><CarOutlined /></span>
              <div>
                <Typography.Title level={4} style={{ margin: 0 }}>{stats.totalCars}</Typography.Title>
                <Typography.Text type="secondary">Total Cars Won</Typography.Text>
              </div>
            </Flex>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Flex align="center" gap={8}>
              <span style={{ color: '#52c41a', fontSize: 20 }}><DollarOutlined /></span>
              <div>
                <Typography.Title level={4} style={{ margin: 0 }}>${stats.totalValue.toLocaleString()}</Typography.Title>
                <Typography.Text type="secondary">Total Value (this page)</Typography.Text>
              </div>
            </Flex>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Flex align="center" gap={8}>
              <span style={{ color: '#fa8c16', fontSize: 20 }}><RiseOutlined /></span>
              <div>
                <Typography.Title level={4} style={{ margin: 0 }}>{stats.deliveredCars}</Typography.Title>
                <Typography.Text type="secondary">Delivered (this page)</Typography.Text>
              </div>
            </Flex>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Flex align="center" gap={8}>
              <span style={{ color: '#722ed1', fontSize: 20 }}><CalendarOutlined /></span>
              <div>
                <Typography.Title level={4} style={{ margin: 0 }}>${Math.round(stats.avgValue).toLocaleString()}</Typography.Title>
                <Typography.Text type="secondary">Average Value (this page)</Typography.Text>
              </div>
            </Flex>
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={6}>
            <Input
              ref={searchInputRef}
              placeholder="Make, Model, VIN, Lot Number..."
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              suffix={<Typography.Text type="secondary" style={{ fontSize: 11 }}>Ctrl+F / ⌘F</Typography.Text>}
            />
          </Col>
          {user.role === 'super_admin' && (
            <>
              <Col xs={24} md={6}>
                <Select
                  placeholder="Filter by Branch"
                  allowClear
                  style={{ width: '100%' }}
                  value={filterBranch}
                  onChange={(v) => setFilterBranch(v ?? undefined)}
                  options={branchOptions}
                  optionRender={(opt) => (
                    <div>
                      <div>{opt.data.flag} {opt.data.name}</div>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>{opt.data.location}</Typography.Text>
                    </div>
                  )}
                />
              </Col>
              <Col xs={24} md={6}>
                <Select
                  placeholder="Filter by Manager"
                  allowClear
                  showSearch
                  filterOption={false}
                  style={{ width: '100%' }}
                  value={filterManager}
                  onChange={(v) => setFilterManager(v ?? undefined)}
                  onSearch={(v) => handleManagerFilterSearch(v)}
                  loading={managerFilterLoading || managerFilterWaiting}
                  notFoundContent={managerFilterWaiting ? 'Searching...' : 'Type at least 2 characters'}
                  options={managerFilterOptions}
                  optionRender={(opt) => (
                    <div>
                      <div>{opt.data.name}</div>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>{opt.data.branchName}</Typography.Text>
                    </div>
                  )}
                />
              </Col>
              <Col xs={24} md={6}>
                <Select
                  placeholder="Filter by Dealer"
                  allowClear
                  showSearch
                  filterOption={false}
                  style={{ width: '100%' }}
                  value={filterDealer}
                  onChange={(v) => setFilterDealer(v ?? undefined)}
                  onSearch={(v) => handleDealerFilterSearch(v)}
                  loading={dealerFilterLoading || dealerFilterWaiting}
                  notFoundContent={dealerFilterWaiting ? 'Searching...' : 'Type at least 2 characters'}
                  options={dealerFilterOptions}
                  optionRender={(opt) => (
                    <div>
                      <div>{opt.data.name}</div>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>{opt.data.branchName}</Typography.Text>
                    </div>
                  )}
                />
              </Col>
            </>
          )}
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredCars}
          rowKey={(r) => r.id || r.lotNumber}
          loading={loading}
          pagination={false}
          onRow={(record) => ({
            onClick: () => handleCarClick(record),
            style: { cursor: 'pointer' }
          })}
          locale={{ emptyText: 'No cars found matching your criteria' }}
          scroll={{ x: 800 }}
        />
      </Card>

      {pagination && pagination.totalPages > 1 && (
        <Flex justify="center" align="center" vertical gap={16} style={{ marginTop: 24 }}>
          <Pagination
            current={currentPage}
            total={pagination.totalLots}
            pageSize={limit}
            showSizeChanger={false}
            showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} total cars`}
            onChange={(page) => {
              setCurrentPage(page);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </Flex>
      )}

      <Modal
        title={`Car Details - ${selectedCar?.year} ${selectedCar?.make} ${selectedCar?.model}`}
        open={carDetailOpen}
        onCancel={() => setCarDetailOpen(false)}
        footer={[
          <Button key="close" onClick={() => setCarDetailOpen(false)}>Close</Button>
        ]}
        width={isMobile ? '100%' : 720}
        style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100%' } : { top: 20 }}
        bodyStyle={isMobile ? { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto' } : {}}
      >
        {selectedCar && (
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
                  <Typography.Text strong><PictureOutlined style={{ marginRight: 8 }} />Car Images ({selectedCar.images?.length || 0})</Typography.Text>
                  <Button
                    type="primary"
                    icon={<CloudUploadOutlined />}
                    onClick={() => fileInputRef.current?.click()}
                    loading={uploadingImages}
                    size="small"
                  >
                    {uploadingImages ? 'Uploading...' : 'Upload Images'}
                  </Button>
                  <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" multiple style={{ display: 'none' }} />
                </Flex>

                {uploadingImages && (
                  <Flex justify="center" align="center" style={{ padding: 24 }}>
                    <Spin size="small" style={{ marginRight: 8 }} />
                    <Typography.Text>Uploading images...</Typography.Text>
                  </Flex>
                )}

                {selectedCar.images && selectedCar.images.length > 0 ? (
                  <Row gutter={[8, 8]}>
                    {selectedCar.images.map((image, index) => (
                      <Col xs={12} sm={8} md={6} key={image.key || index}>
                        <div style={{ position: 'relative', paddingTop: '75%', borderRadius: 4, overflow: 'hidden', border: '1px solid #d9d9d9' }}>
                          <img
                            src={image.url}
                            alt={`Car ${index + 1}`}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                            onClick={() => window.open(image.url, '_blank')}
                          />
                          {(user.role === 'super_admin' || user.role === 'manager') && (
                            <Button
                              type="primary"
                              danger
                              size="small"
                              icon={deletingImage === image.key ? <Spin size="small" /> : <DeleteOutlined />}
                              loading={deletingImage === image.key}
                              style={{ position: 'absolute', top: 4, right: 4 }}
                              onClick={() => handleImageDelete(image.key)}
                            />
                          )}
                          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)', color: 'white', padding: 4, fontSize: 11 }}>
                            {new Date(image.uploadedAt).toLocaleDateString()}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <Flex vertical align="center" gap={8} style={{ padding: 32, color: '#8c8c8c' }}>
                    <PictureOutlined style={{ fontSize: 48, opacity: 0.3 }} />
                    <Typography.Text>No images uploaded yet</Typography.Text>
                    <Typography.Text type="secondary">Click "Upload Images" to add photos</Typography.Text>
                  </Flex>
                )}
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Typography.Title level={5}>Vehicle Information</Typography.Title>
              <div style={{ marginBottom: 16 }}>
                <Flex align="center" gap={4}><Typography.Text><strong>VIN:</strong> {selectedCar.vin}</Typography.Text>{selectedCar.vin && <VinCopy vin={selectedCar.vin} size="small" />}</Flex>
                <Flex align="center" gap={4}><Typography.Text><strong>Lot Number:</strong> {selectedCar.lotNumber}</Typography.Text>{selectedCar.lotNumber && <LotCopy lotNumber={selectedCar.lotNumber} size="small" />}</Flex>
                <Typography.Text><strong>Year:</strong> {selectedCar.year}</Typography.Text><br />
                <Typography.Text><strong>Make:</strong> {selectedCar.make}</Typography.Text><br />
                <Typography.Text><strong>Model:</strong> {selectedCar.model}</Typography.Text><br />
                <Typography.Text><strong>Color:</strong> {selectedCar.color}</Typography.Text><br />
                <Typography.Text><strong>Mileage:</strong> {(selectedCar.mileage || 0).toLocaleString()} miles</Typography.Text><br />
                <Typography.Text><strong>Condition:</strong> {selectedCar.condition}</Typography.Text>
              </div>
              <Typography.Title level={5}>Auction Details</Typography.Title>
              <div>
                <Typography.Text><strong>Auction Site:</strong> {selectedCar.auctionSite}</Typography.Text><br />
                <Typography.Text><strong>Winning Bid:</strong> ${(selectedCar.winningBid || 0).toLocaleString()}</Typography.Text><br />
                <Typography.Text><strong>Fees:</strong> ${(selectedCar.fees || 0).toLocaleString()}</Typography.Text><br />
                <Typography.Text><strong>Total Cost:</strong> ${(selectedCar.totalCost || 0).toLocaleString()}</Typography.Text><br />
                <Flex align="center" gap={8} style={{ marginTop: 8 }}>
                  <Typography.Text><strong>Payment Status:</strong></Typography.Text>
                  <Tag icon={selectedCar.paymentStatus === 'Paid' ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={selectedCar.paymentStatus === 'Paid' ? 'success' : 'error'}>
                    {selectedCar.paymentStatus || 'Unpaid'}
                  </Tag>
                </Flex>
                {selectedCar.paymentStatus !== 'Paid' && selectedCar.unpaidAmountInDollars !== undefined && (
                  <Typography.Text type={selectedCar.unpaidAmountInDollars > 0 ? 'danger' : 'success'} style={{ fontWeight: 500 }}><strong>Unpaid Amount:</strong> ${selectedCar.unpaidAmountInDollars.toLocaleString()}</Typography.Text>
                )}<br />
                <Typography.Text><strong>Won Date:</strong> {new Date(selectedCar.wonDate).toLocaleDateString()}</Typography.Text>
              </div>
            </Col>

            <Col xs={24} md={12}>
              <Typography.Title level={5}>Winning Information</Typography.Title>
              <div style={{ marginBottom: 16 }}>
                <Typography.Text><strong>Won By:</strong> {selectedCar.wonByName}</Typography.Text><br />
                <Typography.Text><strong>Role:</strong> {selectedCar.wonBy}</Typography.Text><br />
                <Typography.Text><strong>Branch:</strong> {selectedCar.branchName}</Typography.Text><br />
                {selectedCar.managerName && <Typography.Text><strong>Manager:</strong> {selectedCar.managerName}</Typography.Text>}
                {selectedCar.dealerName && <Typography.Text><strong>Dealer:</strong> {selectedCar.dealerName}</Typography.Text>}
                {selectedCar.subdealerName && <Typography.Text><strong>Subdealer:</strong> {selectedCar.subdealerName}</Typography.Text>}
              </div>
              <Typography.Title level={5}>Delivery Status</Typography.Title>
              <div style={{ marginBottom: 16 }}>
                <Flex align="center" gap={8}><Typography.Text><strong>Status:</strong></Typography.Text><Tag color={getStatusColor(selectedCar.status)}>{selectedCar.status.replace('_', ' ').toUpperCase()}</Tag></Flex>
                <Typography.Text><strong>Location:</strong> {selectedCar.location}</Typography.Text><br />
                {selectedCar.deliveryDate && <Typography.Text><strong>Delivery Date:</strong> {new Date(selectedCar.deliveryDate).toLocaleDateString()}</Typography.Text>}
              </div>
              <Typography.Title level={5}>Description</Typography.Title>
              <Typography.Text>{selectedCar.description}</Typography.Text>
            </Col>
          </Row>
        )}
      </Modal>

      <Modal
        title={
          <Flex justify="space-between" align="center">
            <span>Update Purchased Car</span>
            <Button type="text" icon={<CloseOutlined />} onClick={handleCloseEditModal} />
          </Flex>
        }
        open={editModalOpen}
        onCancel={handleCloseEditModal}
        footer={[
          <Button key="cancel" onClick={handleCloseEditModal} disabled={updating}>Cancel</Button>,
          <Button key="submit" type="primary" onClick={handleUpdateAssignment} disabled={!assignToId || updating} loading={updating}>
            {updating ? 'Updating...' : 'Update Assignment'}
          </Button>
        ]}
        width={isMobile ? '100%' : 480}
        style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100%' } : { top: 20 }}
        bodyStyle={isMobile ? { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto' } : {}}
      >
        {editingCar && (
          <>
            <Card size="small" style={{ marginBottom: 16 }}>
              <Typography.Text type="secondary">Car Info</Typography.Text>
              <Typography.Text strong style={{ display: 'block' }}>{editingCar.year} {editingCar.make} {editingCar.model}</Typography.Text>
              <Flex align="center" gap={4}>
                <Typography.Text type="secondary">Lot: {editingCar.lotNumber} | VIN: {editingCar.vin}</Typography.Text>
                {editingCar.vin && <VinCopy vin={editingCar.vin} size="small" />}
              </Flex>
              <Typography.Text style={{ color: '#1e3a5f', marginTop: 4, display: 'block' }}>Current: {editingCar.wonByName || editingCar.userName || editingCar.dealerName}</Typography.Text>
              {editingCar.paymentStatus !== 'Paid' && editingCar.unpaidAmountInDollars !== undefined && (
                <Typography.Text type={editingCar.unpaidAmountInDollars > 0 ? 'danger' : 'success'} style={{ fontWeight: 500 }}>Unpaid Amount: ${editingCar.unpaidAmountInDollars.toLocaleString()}</Typography.Text>
              )}
            </Card>

            <Form layout="vertical">
              <Form.Item label="Payment Status">
                <Select
                  value={paymentStatus}
                  onChange={(v) => setPaymentStatus(v)}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'Paid', label: 'Paid' },
                    { value: 'Unpaid', label: 'Unpaid' }
                  ]}
                />
              </Form.Item>

              <Form.Item label="Assign To">
                <Select
                  value={assignType}
                  onChange={(v) => {
                    setAssignType(v);
                    setAssignToId('');
                    setSelectedDealer(null);
                    setSelectedSubdealer(null);
                  }}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'dealer', label: 'Dealer' },
                    { value: 'subdealer', label: 'Subdealer' }
                  ]}
                />
              </Form.Item>

              {assignType === 'dealer' && (
                <Form.Item label="Select Dealer">
                  <Select
                    showSearch
                    filterOption={false}
                    placeholder="Type to search dealers..."
                    value={assignToId || undefined}
                    onChange={(value, option) => {
                      const dealer = option?.data;
                      setSelectedDealer(dealer || null);
                      setAssignToId(value || '');
                    }}
                    onSearch={(v) => handleDealerSearch(v, 'input')}
                    loading={dealerSearchLoading || dealerSearchWaiting}
                    notFoundContent={dealerSearchWaiting ? 'Searching...' : 'Type at least 2 characters'}
                    style={{ width: '100%' }}
                    allowClear
                    onClear={() => { setSelectedDealer(null); setAssignToId(''); }}
                    optionRender={(opt) => (
                      <div>
                        <div>{opt.data?.name}</div>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>{opt.data?.branchName} (Dealer ID: {opt.data?.dealerId || opt.data?.autoIncrementId || opt.data?.id || 'N/A'})</Typography.Text>
                      </div>
                    )}
                    options={dealerOptionsForSelect}
                  />
                </Form.Item>
              )}

              {assignType === 'subdealer' && (
                <Form.Item label="Select Subdealer">
                  <Select
                    showSearch
                    filterOption={false}
                    placeholder="Type to search subdealers..."
                    value={assignToId || undefined}
                    onChange={(value, option) => {
                      const sub = option?.data;
                      setSelectedSubdealer(sub || null);
                      setAssignToId(value || '');
                    }}
                    onSearch={(v) => handleSubdealerSearch(v, 'input')}
                    loading={subdealerSearchLoading || subdealerSearchWaiting}
                    notFoundContent={subdealerSearchWaiting ? 'Searching...' : 'Type at least 2 characters'}
                    style={{ width: '100%' }}
                    allowClear
                    onClear={() => { setSelectedSubdealer(null); setAssignToId(''); }}
                    optionRender={(opt) => (
                      <div>
                        <div>{opt.data?.name}</div>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>Dealer: {opt.data?.dealerName} | Branch: {opt.data?.branchName}</Typography.Text>
                      </div>
                    )}
                    options={subdealerOptionsForSelect}
                  />
                </Form.Item>
              )}
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default WinCarsPage;
